import styled, { useTheme } from 'styled-components'
import { type MembershipPricingData } from '@/features/cms/components/CMS/types'
import { Box, Column, Stack } from '@shipt/design-system-layouts'
import { Body, Headline, Link } from '@shipt/design-system-typography'
import { screenSizes } from '@shipt/design-system-themes'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { MembershipPricingCard } from '@/features/cms/components/CMS/components/MembershipPricing/MembershipPricingCard'
import { ReferenceRenderer } from '@/features/cms/components/CMS/ReferenceRenderer'
import { spacing } from '@/features/shared/theme/tokens'
import { renderElement, renderElementInverse } from '@/features/cms/components/CMS/RichTextRenderer'
import { Carousel } from '@/features/shop/components/Carousel'
import { theme } from '@/features/shared/constants/shiptTheme'

/**
 * This component corresponds to the `membership_pricing` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/membership_pricing
 * - **Production** - https://cms.shipt.com/content-type/pool/membership_pricing
 */
export const MembershipPricing = ({
  content_type_id,
  id,
  data: {
    heading,
    subheading,
    subheading_rich_text,
    legal_text,
    legal_text_cta_link,
    legal_rich_text,
    background_color,
    references,
    carousel_pricing_cards = false,
    pricing_subsections,
    theme: themeMode,
  },
}: MembershipPricingData) => {
  const designTheme = useTheme()

  const getBackgroundColor = () => {
    if (themeMode === 'light') {
      return '#F6F5F6'
    } else if (themeMode === 'dark') {
      return designTheme.color.background.base.brand.primary
    } else {
      return background_color
    }
  }

  return (
    <SectionWrapper
      $background_color={getBackgroundColor()}
      className={background_color || themeMode ? 'with-background' : undefined}
      {...getCMSWrapperProps({ content_type_id, id })}
    >
      <div id="pricing-plans">
        <HeadingColumn align="center" spacing="sm">
          <Headline
            as="h2"
            size="xl"
            {...(themeMode === 'dark' && {
              concept: 'accent',
              surface: 'inverse',
              variant: 'secondary',
            })}
          >
            {heading}
          </Headline>
          {!!subheading && !subheading_rich_text && <Body size="xl">{subheading}</Body>}
          {!!subheading_rich_text && (
            <div>
              {subheading_rich_text.elements.map(
                themeMode === 'dark' ? renderElementInverse : renderElement,
              )}
            </div>
          )}
        </HeadingColumn>
        <PricingContentColumn $cardsLength={pricing_subsections.length}>
          {/* this should display if the screen size is mobile and the carousel field is true */}
          {!!carousel_pricing_cards && (
            <CarouselWrapper>
              <Carousel
                freeMode
                surface={themeMode === 'dark' ? 'inverse' : 'default'}
                simulateTouch
                pagination={{ el: '.swiper-pagination', clickable: true }}
                slidesOffsetBefore={16}
                slidesOffsetAfter={16}
              >
                {pricing_subsections.map((section, index) => (
                  <MembershipPricingCard
                    {...section}
                    index={index}
                    key={section.data.membership_type}
                    isCarousel
                    themeMode={themeMode}
                  />
                ))}
              </Carousel>
            </CarouselWrapper>
          )}
          {/* this should display if the carousel feature is false or the screen is not mobile */}
          <PricingCardsStack
            spacing="lg"
            direction={{ base: 'column', md: 'row' }}
            data-is-carousel={carousel_pricing_cards ?? false}
          >
            {pricing_subsections.map((section, index) => (
              <MembershipPricingCard
                {...section}
                index={index}
                key={section.data.membership_type}
                themeMode={themeMode}
              />
            ))}
          </PricingCardsStack>
          {!!references && (
            <Box marginBottom="lg">
              <ReferenceRenderer references={references} />
            </Box>
          )}
        </PricingContentColumn>
        {!!legal_text && !legal_rich_text && (
          <LegalText size="sm" variant="secondary" id="legal_text">
            {legal_text}{' '}
            {!!legal_text_cta_link && (
              <Link
                size="sm"
                variant="secondary"
                aria-describedby="legal_text"
                href={legal_text_cta_link.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {legal_text_cta_link.text}
              </Link>
            )}
          </LegalText>
        )}
        {!!legal_rich_text && (
          <LegalText as="div">
            {legal_rich_text.elements.map(
              themeMode === 'dark' ? renderElementInverse : renderElement,
            )}
          </LegalText>
        )}
      </div>
    </SectionWrapper>
  )
}
const CarouselWrapper = styled.div`
  .swiper-pagination-bullet {
    background-color: ${theme.gray600};
    opacity: 1;
  }

  .swiper-pagination-bullet-active.swiper-pagination-bullet {
    background-color: ${theme.gray300};
    opacity: 1;
  }

  position: relative;
  margin: 0 -1rem 1rem;

  /* hide the carousel on non-mobile screens */
  @media ${screenSizes.tablet} {
    display: none;
  }
`

const SectionWrapper = styled(GridLayoutContainer)<{
  $background_color?: string | null
}>`
  background-color: ${({ $background_color }) => $background_color};
  scroll-margin-top: 84px;
`

const HeadingColumn = styled(Column)`
  margin-bottom: ${spacing('xl')};
  text-align: center;
`

const PricingContentColumn = styled(Column)<{ $cardsLength: number }>`
  margin: ${spacing(0, 'auto')};
  max-width: ${({ $cardsLength }) => `calc(22.5rem * ${$cardsLength})`};

  @media ${screenSizes.tablet} {
    & > * {
      max-width: 100%;
    }
  }
`

const PricingCardsStack = styled(Stack)<{ 'data-is-carousel': boolean }>`
  padding: ${spacing(0)};
  margin: ${spacing(0)};
  margin-bottom: ${spacing('lg')};
  justify-content: center;

  @media ${screenSizes.tablet} {
    margin-bottom: ${spacing('xl')};
  }

  /* Hide this stack on small screens if the carousel feature is active */
  @media ${screenSizes.max_mobile} {
    &[data-is-carousel='true'] {
      display: none;
    }
  }
`

const LegalText = styled(Body)`
  text-align: center;

  @media ${screenSizes.tablet} {
    margin: ${spacing(0, 'auto')};
  }
`
